import React from "react";
import { AsyncSelect } from "@components/common/SelectAsync";
import { TagsInput, TextInput, Tooltip, MultiSelect, Divider } from "@mantine/core";
import { getOrgsOfDistributor } from "@utils/api-communication/RestApiCli";
import { accountTypeValues } from "types/accountManagementTypes";
import { withDemoAndDistributorTypes } from "@constants/accountTypeOptions";
import { checkRole } from "@utils/CommonUtil";
import { Role } from "@model/role";

export type OrganizationFormProps = {
  selectedOrgId: string | null;
  onSelectedOrgIdChange: (id: string | null) => void;
  newOrgName: string;
  onNewOrgNameChange: (name: string) => void;
  // Pass Mantine form.getInputProps directly to bind inputs
  getInputProps: (path: string) => any;
};

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  selectedOrgId,
  onSelectedOrgIdChange,
  newOrgName,
  onNewOrgNameChange,
  getInputProps,
}) => {
  const accountTypeOptions = withDemoAndDistributorTypes(
    accountTypeValues,
    checkRole([Role.Admin]),
  ).map((a) => ({
    value: a,
    label: a.toUpperCase(),
  }));

  return (
    <>
      <Tooltip
        label="Official name of the organization that will use the license."
        withArrow
        position="bottom"
        multiline
        w={260}
      >
        <div>
          <AsyncSelect
            label="Search Organization"
            value={selectedOrgId}
            onChange={onSelectedOrgIdChange}
            allowCreate={true}
            onCreateNew={(option) => onNewOrgNameChange(option.label)}
            fetchData={(search = "") =>
              new Promise((resolve, reject) => {
                getOrgsOfDistributor(
                  { search },
                  (resp: any) => {
                    const list = resp?.data || [];
                    resolve(
                      list.map((org: any) => ({
                        value: org.orgId,
                        label: org.name || org.orgId,
                      })),
                    );
                  },
                  (err: any) => reject(err),
                );
              })
            }
            placeholder="Select or create organization..."
          />
        </div>
      </Tooltip>

      {newOrgName === selectedOrgId && (
        <>
          <Divider my="md" />
          <Tooltip label="Name of the organization." withArrow position="bottom" multiline w={260}>
            <div>
              <TextInput
                label="Organization Name"
                placeholder="Enter address"
                {...getInputProps("customOrg.name")}
              />
            </div>
          </Tooltip>

          <Tooltip
            label="Address of the customer who will use the license."
            withArrow
            position="bottom"
            multiline
            w={260}
          >
            <div>
              <TextInput
                label="Address"
                placeholder="Enter address"
                {...getInputProps("customOrg.address")}
              />
            </div>
          </Tooltip>

          <Tooltip
            label="Email address of the customer who will use the license. No emails will be sent to this address."
            withArrow
            position="bottom"
            multiline
            w={300}
          >
            <div>
              <TextInput
                label="Email"
                placeholder="Enter email"
                {...getInputProps("customOrg.email")}
              />
            </div>
          </Tooltip>

          <Tooltip
            label="The credentials of the newly created user for the license will be sent to these email addresses."
            withArrow
            position="bottom"
            multiline
            w={320}
          >
            <div>
              <TagsInput
                label="Emails to send credentials"
                placeholder="Enter email"
                clearable
                {...getInputProps("customOrg.emailsToInform")}
              />
            </div>
          </Tooltip>

          <MultiSelect
            label="Account type"
            placeholder="Select account types"
            data={accountTypeOptions}
            searchable
            clearable
            {...getInputProps("customOrg.accountType")}
          />

          <TextInput
            label="Account info"
            placeholder="Enter account info"
            {...getInputProps("customOrg.accountInfo")}
          />
        </>
      )}
    </>
  );
};

export default OrganizationForm;
